import { cloneDeep } from 'lodash'
import { v4 } from 'uuid'

import { mergeRegistrationWithTabData } from '../helpers/registration-on-event'
import useToasterHelper from '../helpers/ToasterHelper'
import { getConvertedData } from '../models/interface.helper'
import helpers from '../commonHelpers/helpers'

import {
  selectPublishedEventInDb,
  selectRegisterTabData,
  selectRegistration,
  selectRegistrationByDay,
  setRegisterTabData,
  setRegistrationAc,
  setRegistrationsByDay,
} from '../store/registration/registrationSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'

import {
  IRegisterTab,
  ITeamMemberR,
} from '../models/event-registered-users/event-registered-users.interface'
import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../models/registrations-by-day/registrationByDay.interface'
import { RegistrationByDayModel } from '../models/registrations-by-day/registrationByDay.model'
import { IHorseData } from '../models/horse/horse.interface'

import { MESSAGES_CONST } from '../const/messages-const'
import FirestoreService from '../services/firestoreService'
import { CONST } from '../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

type IOnRegistrationMemberChangeFnArgs = {
  currentClass: IRegisterTab
  member: ITeamMemberR | null
  removedUniqId?: string | null
}

const useEventRegister = () => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()

  const publishedEventInDb = useAppSelector(selectPublishedEventInDb)
  const registration = useAppSelector(selectRegistration)
  const registerTabData = useAppSelector(selectRegisterTabData)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)

  const updateRegistrationByDay = async ({
    registrationsByDayToUpdate,
    registrationsByDayToAdd,
    registrationsByDayToDelete,
  }: {
    registrationsByDayToUpdate?: IRegistrationByDayInterface[]
    registrationsByDayToAdd?: IRegistrationByDayInterface[]
    registrationsByDayToDelete?: IRegistrationByDayInterface[]
  }) => {
    if (!registration) return

    let registrationsByDayUpdated: IRegistrationByDayInterface[]
    const registrationsByDayConned: IRegistrationByDayInterface[] = cloneDeep(registrationsByDay)

    if (registrationsByDayToDelete?.[0]) {
      const idsToDelete = registrationsByDayToDelete.map((item) => item.uniqId)

      registrationsByDayUpdated = registrationsByDayConned.filter(
        (registration) => !idsToDelete.includes(registration.uniqId)
      )
    } else if (registrationsByDayToUpdate?.[0]) {
      const idsToUpdate = registrationsByDayToUpdate.map((item) => item.uniqId)

      registrationsByDayUpdated = registrationsByDayConned.map((registrationByDay) => {
        const isUpdate = idsToUpdate.includes(registrationByDay?.uniqId)

        const filtered = registrationsByDayToUpdate.filter(
          (registrationByDayToUpdate) =>
            registrationByDayToUpdate.uniqId === registrationByDay.uniqId
        )

        if (isUpdate) {
          return filtered[0]
        } else {
          return registrationByDay
        }
      })
    } else {
      registrationsByDayUpdated = getConvertedData([
        ...registrationsByDayConned,
        ...(registrationsByDayToAdd || []),
      ])
    }

    const registerTabDataUpdated = mergeRegistrationWithTabData(
      registerTabData,
      registrationsByDayUpdated
    )

    const registrationUpdated = {
      ...registration,
      cartItems: {
        ...registration?.cartItems,
        classes: registrationsByDayUpdated,
      },
    }

    await FirestoreService.updateItem(
      COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
      registration.id,
      registrationUpdated
    )

    dispatch(setRegistrationsByDay(registrationsByDayUpdated))
    dispatch(setRegisterTabData(registerTabDataUpdated))
    dispatch(setRegistrationAc(registrationUpdated))
  }

  const onRegistrationRiderChange = async ({
    member,
    currentClass,
    removedUniqId,
  }: IOnRegistrationMemberChangeFnArgs) => {
    if (!publishedEventInDb || !member || !registration) return null

    const { EventFees } = publishedEventInDb
    const registrationsByDayToDelete: IRegistrationByDayInterface[] = []
    const registrationsByDayToAdd: IRegistrationByDayInterface[] = []

    const registrationsByDayConned: IRegistrationByDayInterface[] = cloneDeep(registrationsByDay)
    try {
      if (removedUniqId) {
        const riderToRemove = cloneDeep(
          currentClass.registrationsByDay.filter((reg) => reg.uniqId === removedUniqId)
        )
        registrationsByDayToDelete.push(...riderToRemove)

        // we need to update riders which related with division
        registrationsByDayConned.forEach((registrationByDay) => {
          if (registrationByDay.divisionUniqId === riderToRemove[0].uniqId)
            registrationsByDayToDelete.push(registrationByDay)
        })
      } else {
        currentClass.registrationsByDay.push(
          new RegistrationByDayModel({
            userId: registration?.userId || null,
            userName: registration?.userName || null,
            eventId: registration?.eventId || null,
            eventName: registration?.eventName || null,

            uuid: currentClass.uuid,

            riderId: member?.memberId || null,
            riderDob: member?.memberDob || null,
            riderName: member?.memberName || null,
            riderNameNGram: member?.memberNameNGram || null,
            riderEmail: member?.memberEmail || null,
            riderProfilePicture: member?.memberProfilePicture || null,
            riderCountry: member?.memberCountry || null,

            horseId: null,
            horseName: null,
            horseProfilePicture: null,
            horsesSelected: [],

            registrationDocId: registration?.id || null,

            registrationPrice: currentClass.price || 0,
            includeSalesTax: publishedEventInDb.EventPaymentSettings?.chargeSalesTax || false,
            registrationByDayName: currentClass.registrationByDayName,
            qualifyFee: currentClass?.qualifyFee || null,
            qualifyFeeName: currentClass?.qualifyFeeName || null,

            isDivision: currentClass.children && currentClass.children.length > 0,
            divisionUniqId: null,
            divisionUuid:
              publishedEventInDb.EventFees?.registrationFees?.find((fee) =>
                fee.children?.find((child) => child.uuid === currentClass.uuid)
              )?.uuid || null,

            paymentStatus: null,
            paymentType: null,
            isScratched: false,
            amountScratched: 0,
            amountRefunded: 0,
            refundDocId: null,
            refundId: null,
            refundStatus: null,

            score: null,
            order: 0,
            backNumber: 0,

            breakTime: EventFees.rideTimeRequirement?.minutes || null,
            uniqId: `${v4()}-${Date.now()}`,

            recipientId: registration?.userId || null,
            recipientName: registration?.userName || null,
            recipientProfilePicture: registration?.userProfilePicture || null,
          }).toObject()
        )

        const newRider = cloneDeep(currentClass.registrationsByDay.slice(-1))[0]
        registrationsByDayToAdd.push(newRider)

        // we need to add riders who related with division
        if (currentClass.children && currentClass.children.length > 0) {
          currentClass.children.forEach((child) => {
            registrationsByDayToAdd.push({
              ...newRider,
              registrationPrice: child.price || 0,
              includeSalesTax: publishedEventInDb.EventPaymentSettings?.chargeSalesTax || false,
              qualifyFee: child?.qualifyFee || null,
              qualifyFeeName: child?.qualifyFeeName || null,
              registrationByDayName: child.registrationByDayName,

              isDivision: false,
              divisionUniqId: newRider.uniqId || null,
              divisionUuid:
                publishedEventInDb.EventFees?.registrationFees?.find(
                  (fee) => fee.uuid === currentClass.uuid
                )?.uuid || null,

              uuid: child.uuid,
              uniqId: `${v4()}-${Date.now()}`,
            })
          })
        }
      }
      await updateRegistrationByDay({
        registrationsByDayToAdd,
        registrationsByDayToDelete,
      })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onRegistrationByDayHorseChange = async ({
    currentRow,
    horse,
    removedIndex,
    noHorse,
  }: {
    horse: IHorseData | null
    currentRow: IRegistrationByDayInterface
    removedIndex?: number
    noHorse?: boolean
  }) => {
    if (!publishedEventInDb || !registration) return null
    const registrationsByDayConned: IRegistrationByDayInterface[] = cloneDeep(registrationsByDay)

    let registrationsByDayToUpdate = registrationsByDayConned.filter(
      (registration) =>
        registration.uniqId === currentRow.uniqId ||
        registration.divisionUniqId === currentRow.uniqId
    )

    try {
      if (!noHorse) {
        const { emptyVarName } = helpers.findEmptyVal({
          horse,
        })
        if (emptyVarName) {
          console.error('Norse not selected', 'error')
          toastFunctions.error({
            message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
          })
          return
        }
      }

      if (removedIndex !== undefined) {
        registrationsByDayToUpdate = registrationsByDayToUpdate.map((rider) => {
          const horsesSelected = [
            ...(rider?.horsesSelected || [])?.slice(0, removedIndex),
            ...(rider?.horsesSelected || [])?.slice(removedIndex + 1),
          ]

          return {
            ...rider,
            horseId: horsesSelected?.[0]?.id || null,
            horseName: horsesSelected?.[0]?.name || null,
            horsesSelected,
            horseProfilePicture: null,
          }
        })
      } else if (noHorse) {
        const horsesSelected = [
          {
            id: HORSE_NAME_DEFAULT,
            name: HORSE_NAME_DEFAULT,
            picture: '',
          },
        ]

        registrationsByDayToUpdate = registrationsByDayToUpdate.map((rider) => ({
          ...rider,
          horseId: removedIndex ? null : HORSE_NAME_DEFAULT,
          horseName: removedIndex ? null : HORSE_NAME_DEFAULT,
          horsesSelected,
          horseProfilePicture: null,
        }))
      } else {
        registrationsByDayToUpdate = registrationsByDayToUpdate.map((rider) => {
          const horsesSelected = [
            ...(rider?.horsesSelected.filter((horse) => horse.id !== HORSE_NAME_DEFAULT) || []),
            {
              id: horse?.id || '',
              name: horse?.horseName || '',
              picture: horse?.horseProfilePicture || '',
            },
          ]

          return {
            ...rider,
            horseId: horse?.id || null,
            horseName: horse?.horseName || null,
            horsesSelected,
            horseProfilePicture: horse?.horseProfilePicture,
          }
        })
      }

      await updateRegistrationByDay({
        registrationsByDayToUpdate,
      })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return {
    onRegistrationRiderChange,
    onRegistrationByDayHorseChange,
    updateRegistrationByDay,
  }
}

export default useEventRegister
